import { useEffect, useRef, useState } from 'react';
import { SectionHeading } from '../../components/SectionHeading';
import { prefersReducedMotion } from '../../hooks';

type StepKind = 'inbound' | 'think' | 'action' | 'result';

interface DemoStep {
  kind: StepKind;
  text: React.ReactNode;
  src?: string;
}

interface Scenario {
  id: string;
  chip: string;
  steps: DemoStep[];
}

const SCENARIOS: Scenario[] = [
  {
    id: 'stock',
    chip: 'Do you have the SPX-200 in stock?',
    steps: [
      { kind: 'inbound', text: '"Do you have the SPX-200 infusion pump in stock?"' },
      { kind: 'think', text: 'reading the question · identifying product code SPX-200' },
      {
        kind: 'action',
        text: (
          <>
            searching <b>knowledge base</b>
          </>
        ),
        src: 'catalog-2026.pdf · p.14',
      },
      {
        kind: 'action',
        text: (
          <>
            calling <b>inventory API</b> · GET /stock/SPX-200
          </>
        ),
      },
      { kind: 'result', text: 'replied: "In stock — 12 units. Want a formal quote?"' },
      {
        kind: 'action',
        text: (
          <>
            tagged contact <b>warm-lead</b>
          </>
        ),
      },
    ],
  },
  {
    id: 'quote',
    chip: 'Can I get a quote for 20 units?',
    steps: [
      { kind: 'inbound', text: '"Can I get a quote for 20 units, delivered next month?"' },
      { kind: 'think', text: 'quantity 20 · needs tier pricing + delivery window' },
      {
        kind: 'action',
        text: (
          <>
            searching <b>knowledge base</b>
          </>
        ),
        src: 'price-list-2026.pdf · tier 20+',
      },
      {
        kind: 'action',
        text: (
          <>
            drafting quote in <b>Lumen ERP</b>
          </>
        ),
      },
      { kind: 'result', text: 'sent quote #Q-4471 as PDF · valid 30 days' },
      {
        kind: 'action',
        text: (
          <>
            scheduled <b>follow-up</b> · in 2 days if no reply
          </>
        ),
      },
    ],
  },
  {
    id: 'human',
    chip: 'This is urgent — I need a person.',
    steps: [
      { kind: 'inbound', text: '"Our unit failed mid-procedure. I need someone now."' },
      { kind: 'think', text: 'detected urgency + clinical risk · outside answerable scope' },
      {
        kind: 'action',
        text: (
          <>
            classified <b>escalate — do not auto-reply</b>
          </>
        ),
      },
      {
        kind: 'action',
        text: (
          <>
            paged <b>on-call engineer</b> · full transcript attached
          </>
        ),
      },
      { kind: 'result', text: 'replied: "A specialist is being paged now — one moment."' },
      { kind: 'result', text: 'human took over in 40 seconds' },
    ],
  },
];

export function AgentDemo(): React.JSX.Element {
  const [active, setActive] = useState(0);
  const [shown, setShown] = useState(0);
  const [started, setStarted] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  const scenario = SCENARIOS[active] as Scenario;
  const isStatic = prefersReducedMotion();

  // Start the first run when the demo scrolls into view.
  useEffect(() => {
    if (started) return;
    if (isStatic) {
      setStarted(true);
      setShown(scenario.steps.length);
      return;
    }
    const el = sectionRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setStarted(true);
          io.disconnect();
        }
      },
      { threshold: 0.35 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [started, isStatic, scenario.steps.length]);

  // Reveal steps one at a time.
  useEffect(() => {
    if (!started || isStatic) return;
    if (shown >= scenario.steps.length) return;
    const kind = scenario.steps[shown]?.kind;
    const delay = shown === 0 ? 350 : kind === 'think' ? 900 : kind === 'result' ? 800 : 650;
    const t = window.setTimeout(() => setShown((n) => n + 1), delay);
    return () => window.clearTimeout(t);
  }, [started, isStatic, shown, scenario.steps]);

  const pick = (i: number): void => {
    setActive(i);
    setShown(isStatic ? SCENARIOS[i]!.steps.length : 0);
    setStarted(true);
  };

  const done = shown >= scenario.steps.length;

  return (
    <section id="demo">
      <div className="container">
        <SectionHeading
          anim="wipe"
          title={
            <>
              Try it. <span className="si accent-text">Ask the agent something</span>.
            </>
          }
          lede="This is the loop every Inafana agent runs: understand, retrieve, act, and know when to hand over to a human."
        />

        <div className="demo-grid" ref={sectionRef}>
          <div className="demo-picker reveal">
            <p className="demo-picker-label">Pick a customer message</p>
            {SCENARIOS.map((s, i) => (
              <button
                key={s.id}
                type="button"
                className={`demo-chip ${i === active ? 'active' : ''}`}
                onClick={() => pick(i)}
                aria-pressed={i === active}
              >
                {s.chip}
              </button>
            ))}
            <p className="demo-hint">
              Every step below is a real capability — retrieval, API calls, CRM writes,
              and escalation rules you define.
            </p>
          </div>

          <div className="demo-console reveal" data-delay="1">
            <div className="demo-console-head">
              <span className="demo-console-title">Agent trace</span>
              <span className={`demo-status ${done ? 'done' : ''}`}>
                {done ? 'resolved' : 'working…'}
              </span>
            </div>
            <div className="demo-console-body" aria-live="polite">
              {scenario.steps.map((step, i) => (
                <div
                  key={`${scenario.id}-${i}`}
                  className={`demo-step ${step.kind} ${i < shown ? 'shown' : ''}`}
                >
                  <span className="demo-step-tag">
                    {step.kind === 'inbound'
                      ? 'in'
                      : step.kind === 'think'
                        ? 'think'
                        : step.kind === 'action'
                          ? 'act'
                          : 'out'}
                  </span>
                  <span className="demo-step-text">
                    {step.text}
                    {step.src && <span className="demo-step-src">source: {step.src}</span>}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
